import React from 'react';
import {
  Box,
  TextInput,
  Button,
  ArrowLeftIcon,
  ActionList,
  DropdownOverlay,
  Dropdown,
  ActionListItem,
  SelectInput,
  useToast,
} from '@razorpay/blade/components';
import { useForm } from 'forms';
import { useHistory } from 'react-router-dom';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from '../components/styles';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';
import { routePaths } from 'components/Routes/data';
import { CompensationFormFields } from './types';
import {
  compensationValidationSchema,
  getAmountPayload,
  getCompensationStepPayload,
  invalidateDraftEmployeeCache,
  updateInitialCompensationFormData,
} from './utils';
import { initialCompensationData } from './data';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { CustomSalaryStructureForm } from './components/salaryStructure/CustomSalaryStructureForm';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'api';
import { AppError } from 'utils/AppError';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { useEmployeeCustomSalaryStructure } from './components/salaryStructure/hooks/useEmployeeCustomSalaryStructure';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { useListSalaryStructures } from 'components/SalaryStructures/hooks';

import { SalaryStructure } from 'components/SalaryStructure/types';

import SalaryStructureDropDown from 'components/SalaryStructures/creation/components/SalaryStructureDropDown';

export const CompensationDetails = ({
  setShowDiscardModal,
  setShowSaveDraftAndCloseModal,
}: {
  setShowDiscardModal: (value: boolean) => void;
  setShowSaveDraftAndCloseModal: (value: boolean) => void;
}) => {
  const { draftEmployeeId } = useDraftEmployeeId();
  const { register, formData, validate, setIsSubmitting, isSubmitting, setFormData } =
    useForm<CompensationFormFields>(initialCompensationData);
  const history = useHistory();
  const { isMileStone1Enabled: isRearchEnabled, isM3S1Enabled } = useIsFeatureDisabledForRearch();
  const isRearchDisabled = !isRearchEnabled;
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleStepBack = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.basicDetails,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const { isLoading, isFetching, data } = useDraftEmployeeData({
    onSuccess: (data) => updateInitialCompensationFormData(data, setFormData),
  });
  const {
    data: salaryStructureList,
    isFetching: isFetchingSalaryStructureList,
    error: listSalaryStructureError,
  } = useListSalaryStructures({
    queryKey: ['salaryStructuresForEmployeeOnboarding'],
    filters: 'SALARY_STRUCTURE_STATUS_ACTIVE',
    enabled: isM3S1Enabled && !isFetching,
    onSuccess: (salaryStructures: SalaryStructure[]) => {
      const defaultSalaryStructureId: string =
        salaryStructures?.find((item) => item?.type === 'SALARY_STRUCTURE_TYPE_DEFAULT')?.id || '';

      let salaryStructureId: string;

      if (!data?.compensationDetails?.salaryStructureId) {
        salaryStructureId = defaultSalaryStructureId as string;
      } else {
        const currentSalaryStructure = data?.compensationDetails?.salaryStructureId;
        const isSalaryStructureDisabled = !salaryStructures?.find(
          (item) => item?.id === currentSalaryStructure,
        )?.id;

        salaryStructureId = isSalaryStructureDisabled ? '' : currentSalaryStructure;
      }

      setFormData({ ...formData, salaryStructureId: [salaryStructureId] });
    },
  });

  const templates = salaryStructureList ?? [];

  const employeeCustomSalaryStructureHandlers = useEmployeeCustomSalaryStructure({
    compensationDetails: data?.compensationDetails,
  });

  const { mutateAsync: updateDraftEmployeeCompensationStep } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.updateDraftEmployeeCompensationStep,
    onError: (error: typeof AppError) => {
      setIsSubmitting(false);
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent, onSuccess: () => void) => {
    e.preventDefault();

    const isValidForm = validate(compensationValidationSchema);
    const validateSalaryStructureId = isM3S1Enabled && !formData.salaryStructureId?.[0]?.trim();
    if (!isValidForm || validateSalaryStructureId) {
      return;
    }
    setIsSubmitting(true);

    let salaryStructureType = formData.salaryStructureType?.[0] ?? 'default';
    let salaryStructureId = formData.salaryStructureId?.[0];
    if (isRearchEnabled && !isM3S1Enabled) {
      salaryStructureType = 'default';
    }

    try {
      const res = await updateDraftEmployeeCompensationStep({
        draftEmployeeId,
        input: {
          ...(isM3S1Enabled ? { salaryStructureId } : {}),
          salaryStructure: salaryStructureType,
          ctc:
            formData.salaryStructureType?.[0] === 'default' || isM3S1Enabled
              ? getAmountPayload(formData.annualCtc)
              : undefined,
          variablePay: isRearchDisabled ? getAmountPayload(formData.variablePay) : undefined,
          component:
            salaryStructureType === 'custom'
              ? getCompensationStepPayload(employeeCustomSalaryStructureHandlers.components)
              : undefined,
        },
      });

      if (res.payrollUpdateDraftEmployeeCompensation.data.id) {
        onSuccess();
      }
    } finally {
      setIsSubmitting(false);
      invalidateDraftEmployeeCache(queryClient, draftEmployeeId);
    }
  };

  const handleContinueSuccess = () => {
    if (isM3S1Enabled) {
      history.push({
        pathname: routePaths.employeeOnboarding.employee.compensationBreakdown,
        search: `?draftId=${draftEmployeeId}`,
      });
    } else
      history.push({
        pathname: routePaths.employeeOnboarding.employee.compliance,
        search: `?draftId=${draftEmployeeId}`,
      });
  };

  const handleSaveAndCloseSuccess = () => {
    setShowSaveDraftAndCloseModal(true);
  };

  const shouldDisableButtons =
    isLoading || isFetching || isSubmitting || isFetchingSalaryStructureList;

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />
      <StyledForm onSubmit={(e) => handleSubmit(e, handleContinueSuccess)}>
        <FormFieldsWrapper shouldShowSpinner={isLoading || isFetching}>
          <FormHeading
            title="Compensation Details"
            description="Calculate the employee's gross pay for payroll processing"
          />
          <BorderedBox>
            {isRearchDisabled && (
              <Dropdown selectionType="single">
                <SelectInput
                  label="Salary Structure"
                  labelPosition="top"
                  {...register('salaryStructureType', 'select')}
                />
                <DropdownOverlay>
                  <ActionList>
                    <ActionListItem title="Default Salary Structure" value="default" />
                    <ActionListItem title="Custom Salary Structure" value="custom" />
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            )}

            {isM3S1Enabled && (
              <SalaryStructureDropDown
                required={true}
                structures={templates}
                registerProps={register('salaryStructureId', 'select')}
              />
            )}

            {formData.salaryStructureType?.[0] === 'default' && (
              <TextInput
                {...register('annualCtc')}
                label="Annual CTC"
                autoFocus
                placeholder="0"
                size="medium"
                necessityIndicator="required"
                isRequired
                type="number"
                prefix="₹"
              />
            )}

            {isRearchDisabled && (
              <TextInput
                {...register('variablePay')}
                label="Variable Pay"
                placeholder="0"
                size="medium"
                type="number"
                prefix="₹"
                helpText="Annual bonus, incentive etc. that is not included in the monthly salary by default. This is in addition to the Annual CTC"
              />
            )}
          </BorderedBox>

          {formData.salaryStructureType?.[0] === 'custom' && (
            <CustomSalaryStructureForm
              isFormBeingSubmitted={shouldDisableButtons}
              employeeCustomSalaryStructureHandlers={employeeCustomSalaryStructureHandlers}
            />
          )}
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={handleStepBack}
            isDisabled={shouldDisableButtons}>
            Back
          </Button>
          <Box display="flex" gap="spacing.3">
            <Button
              variant="secondary"
              size="medium"
              onClick={(e) => handleSubmit(e, handleSaveAndCloseSuccess)}
              isLoading={shouldDisableButtons}>
              Save and Close
            </Button>
            <Button type="submit" variant="primary" size="medium" isLoading={shouldDisableButtons}>
              Continue
            </Button>
          </Box>
        </FormFooterWrapper>
      </StyledForm>
    </>
  );
};
